import Arrow from 'assets/svg/cancel/cancel-icon.svg'
import Badge from 'common/components/Badge'
import Button from 'common/components/Button'
import Flex from 'common/components/Flex'
import Image from 'common/components/Image'
import Modal from 'common/components/Modal'
import Typography from 'common/components/Typography'
import theme from 'common/theme'
import eventTracking from 'lib/event-tracking'
import { GAUniversalEvent } from 'lib/google-analytics'
import trackPosthog from 'lib/posthog/tracker'
import { useEffect, useState } from 'react'
import FailedIcon from 'services/Auth/assets/svg/error/error-illustration.svg'
import SuccessIcon from 'services/Profile/assets/svg/profile-card-success.svg'
import IdProfiling from 'services/Questionnaire/constants'
import GAEvent from 'services/Questionnaire/event/google-analytics'
import { validateFields } from 'services/Questionnaire/widget/ModalQuestionnaireWrapper/utils'
import QuestionnaireWrapper from 'services/Questionnaire/widget/QuestionnaireWrapper'
import { useStoreActions, useStoreState } from 'stores/hooks'
import styled from 'styled-components'

const StyledButton = styled(Button)`
  font-size: 16px;
  min-height: 54px;
`
const CloseButton = styled(Arrow)`
  transform-origin: 50% 50%;
  transform: scale(1.5) rotate(270deg);
  margin-right: 16px;
  cursor: pointer;
  position: relative;
  top: 2px;
  path {
    fill: ${theme.colors.black};
  }
`
const ModalWrapper = styled(Flex)`
  max-height: 600px;
  overflow: scroll;
`
const HeaderWrapper = styled.div`
  margin-bottom: 20px;
`

interface IModalQuestionnaireWrapper {
  isOpen?: boolean
  id: string
  onBackClick?: () => void
  onSuccess?: () => void
  onClose?: () => void
}

function ModalQuestionnaireWrapper({
  isOpen,
  id,
  onBackClick,
  onSuccess,
  onClose,
}: IModalQuestionnaireWrapper): JSX.Element {
  const [isDisabled, setIsDisabled] = useState<boolean>(true)
  const [isShowSuccessMessage, setShowSuccessMessage] = useState<
    boolean
  >(false)
  const [isShowFailedMessage, setShowFailedMessage] = useState<
    boolean
  >(false)
  const [isLastPage, setIsLastPage] = useState<boolean>(false)

  const {
    answerList,
    data,
    selectedPage,
    questionnaireId,
  } = useStoreState((state) => state.questionnaire)

  const {
    submitQuestionnaire,
    resetQuestionnaire,
    setNextPage,
    setPreviousPage,
  } = useStoreActions((actions) => actions.questionnaire)

  const tracker = eventTracking(trackPosthog())

  useEffect(() => {
    if (selectedPage + 1 === data?.length) {
      return setIsLastPage(true)
    }
    return setIsLastPage(false)
  }, [selectedPage, data])

  useEffect(() => {
    // checking questionnaire if there is null/blank answer
    let mustDisabled = false

    const callback = () => {
      mustDisabled = true
    }

    validateFields(data, selectedPage, answerList, callback)
    setIsDisabled(mustDisabled)
  }, [answerList, data, selectedPage])

  const handleSubmit = async () => {
    if (isShowFailedMessage || isShowSuccessMessage) {
      resetQuestionnaire()
    } else {
      if (GAEvent.submit[data?.[selectedPage]?.group_name]) {
        GAUniversalEvent(
          GAEvent.submit[data?.[selectedPage]?.group_name].category,
          GAEvent.submit[data?.[selectedPage]?.group_name].action,
          GAEvent.submit[data?.[selectedPage]?.group_name].label,
        )
      }
      const trackingParams = {
        Name: 'Questionnaire Submit Button',
        'Questionnaire Id': id,
        'Questionnaire Name': data?.[selectedPage]?.group_name,
        'Questionnaire Page': selectedPage + 1,
      }

      tracker.track('Submitted', trackingParams)

      const response = await submitQuestionnaire(id)
      if (response) {
        setShowSuccessMessage(true)
      } else {
        setShowFailedMessage(true)
      }
    }
  }

  const handleNextPage = () => {
    const trackingParams = {
      Name: 'Questionnaire Next Button',
      'Questionnaire Id': id,
      'Questionnaire Name': data?.[selectedPage]?.group_name,
      'Questionnaire Page': selectedPage + 1,
    }

    tracker.track('Clicked', trackingParams)

    setNextPage()
  }

  const handlePreviousPage = () => {
    setPreviousPage()
  }

  const handleSuccessOkClick = () => {
    resetQuestionnaire()
    tracker.track('Clicked', {
      Name: 'Questionnaire Success OK Button',
    })
    if (onSuccess) onSuccess()
  }

  const handleFailedOkClick = () => {
    resetQuestionnaire()
    tracker.track('Clicked', {
      Name: 'Questionnaire Failed OK Button',
    })
  }

  const renderSuccessMessage = () => {
    return (
      <>
        <Flex justifyContent="center" data-testid="success">
          <SuccessIcon width="135px" />
        </Flex>
        <Typography marginTop="36px" type="bh2" textAlign="center">
          Profil Anda Berhasil Terdaftar!
        </Typography>
        <Typography
          marginTop="15px"
          marginBottom="15px"
          textAlign="center"
        >
          {IdProfiling.profilingAccountType === questionnaireId
            ? 'Terima kasih atas informasi yang Anda berikan, jika Anda ingin mengubah silahkan hubungi Hello Ralali.'
            : 'Terimakasih atas informasi yang Anda berikan.'}
        </Typography>
        <StyledButton
          fontWeight="bold"
          size="large"
          onClick={handleSuccessOkClick}
          margin="15px 0 0"
          block
          data-testid="button-submit-questionnaire"
        >
          OK
        </StyledButton>
      </>
    )
  }

  const renderFailedMessage = () => {
    return (
      <>
        <Flex justifyContent="center" padding="28px 0 0">
          <FailedIcon width="135px" />
        </Flex>
        <Typography marginTop="36px" type="bh2" textAlign="center">
          Terjadi Kesalahan
        </Typography>
        <Typography
          marginTop="10px"
          marginBottom="15px"
          fontSize="13px"
          textAlign="center"
        >
          Mohon maaf terjadi kesalahan, silahkan coba lagi
        </Typography>
        <StyledButton
          fontWeight="bold"
          size="large"
          onClick={handleFailedOkClick}
          margin="15px 0 0"
          block
          data-testid="button-submit-questionnaire"
        >
          OK
        </StyledButton>
      </>
    )
  }

  const renderHeader = () => {
    return (
      <HeaderWrapper>
        {selectedPage !== 0 &&
        data?.[selectedPage]?.group_image_url ? (
          <Flex justifyContent="space-between" alignItems="center">
            <Badge
              content={`${selectedPage + 1}/${data?.length}`}
              variant="alt1"
              position="flex"
              backgroundColor={theme.colors.orange}
              width="49px"
            />
            <Typography marginTop="0px" type="bh2" textAlign="center">
              {data?.[selectedPage]?.group_name}
            </Typography>
            {onClose && (
              <CloseButton
                onClick={onClose}
                data-testid="close-button"
              />
            )}
          </Flex>
        ) : (
          <>
            <Flex justifyContent="space-between">
              <Badge
                content={`${selectedPage + 1}/${data?.length}`}
                variant="alt1"
                position="flex"
                backgroundColor={theme.colors.orange}
                width="49px"
                right="53px"
              />
              <CloseButton
                onClick={onClose}
                data-testid="close-button"
              />
            </Flex>
            {data?.[selectedPage]?.group_image_url && (
              <Flex justifyContent="center">
                <Image
                  lazy
                  src={data?.[selectedPage]?.group_image_url}
                  height="85px"
                  width="130px"
                  objectFit="contain"
                />
              </Flex>
            )}
            <Typography
              marginTop={
                data?.[selectedPage]?.group_image_url ? '20px' : '0px'
              }
              type="bh2"
              textAlign="center"
            >
              {data?.[selectedPage]?.group_name}
            </Typography>
          </>
        )}
        <div style={{ padding: '0px 25px' }}>
          <Typography
            marginTop="10px"
            marginBottom="10px"
            fontSize="13px"
            textAlign="center"
          >
            {data?.[selectedPage]?.group_description}
          </Typography>
        </div>
      </HeaderWrapper>
    )
  }

  const renderFooter = () => {
    return data.length > 1 && selectedPage !== 0 ? (
      <Flex justifyContent="space-between" gap="1.5rem">
        <>
          <StyledButton
            fontWeight="bold"
            size="large"
            onClick={onBackClick || handlePreviousPage}
            margin="10px 0 0"
            block
            data-testid="button-back"
            variant="outlined"
          >
            Kembali
          </StyledButton>
          <StyledButton
            fontWeight="bold"
            size="large"
            onClick={isLastPage ? handleSubmit : handleNextPage}
            margin="10px 0 0"
            block
            data-testid="button-submit-questionnaire"
            disabled={isDisabled}
          >
            {isLastPage ? 'Kirim' : 'Lanjut'}
          </StyledButton>
        </>
      </Flex>
    ) : (
      <StyledButton
        fontWeight="bold"
        size="large"
        onClick={isLastPage ? handleSubmit : handleNextPage}
        margin="10px 0 0"
        block
        data-testid="button-submit-questionnaire"
        disabled={isDisabled}
      >
        {isLastPage ? 'Kirim' : 'Lanjut'}
      </StyledButton>
    )
  }

  return (
    <Modal
      isOpen={isOpen}
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      onOverlayClick={null}
      width="580px"
      height="auto"
      padding="0"
      borderRadius="10px"
    >
      <ModalWrapper
        data-testid="modal-questionnaire"
        flexDirection="column"
        padding="30px 50px 30px"
      >
        {isShowFailedMessage && renderFailedMessage()}
        {isShowSuccessMessage && renderSuccessMessage()}
        {!isShowSuccessMessage && !isShowFailedMessage && (
          <>
            {renderHeader()}
            <QuestionnaireWrapper />
            {renderFooter()}
          </>
        )}
      </ModalWrapper>
    </Modal>
  )
}

export default ModalQuestionnaireWrapper
